import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const LiquidityHeatmap = ({ selectedContract }) => {
  const [heatmapData, setHeatmapData] = useState([]);
  const [selectedPriceLevel, setSelectedPriceLevel] = useState(null);
  const [viewMode, setViewMode] = useState('depth'); // 'depth' or 'volume'

  // Mock heatmap data generation
  const generateHeatmapData = () => {
    const basePrice = selectedContract?.includes('EUA') ? 85.25 : 78.25;
    const data = [];
    
    for (let i = -20; i <= 20; i++) {
      const price = basePrice + (i * 0.05);
      const distance = Math.abs(i);
      const maxLiquidity = Math.max(1000, 5000 - (distance * 150));
      const liquidity = Math.floor(Math.random() * maxLiquidity) + 500;
      const volume = Math.floor(liquidity * (0.8 + Math.random() * 0.4));
      
      data?.push({
        price: price,
        liquidity: liquidity,
        volume: volume,
        orders: Math.floor(liquidity / 200) + Math.floor(Math.random() * 10),
        side: i < 0 ? 'bid' : i > 0 ? 'ask' : 'mid',
        intensity: Math.min(1, liquidity / 4000)
      });
    }
    
    return data?.sort((a, b) => b?.price - a?.price);
  };

  useEffect(() => {
    const updateHeatmap = () => {
      setHeatmapData(generateHeatmapData());
    };

    updateHeatmap();
    const interval = setInterval(updateHeatmap, 3000);
    return () => clearInterval(interval);
  }, [selectedContract]);

  const formatPrice = (price) => {
    return selectedContract?.includes('EUA') ? `€${price?.toFixed(2)}` : `$${price?.toFixed(2)}`;
  };

  const formatQuantity = (quantity) => {
    return quantity?.toLocaleString();
  };

  const getIntensityColor = (intensity, side) => {
    const alpha = Math.max(0.1, intensity);
    if (side === 'bid') {
      return `rgba(34, 197, 94, ${alpha})`; // Green for bids
    } else if (side === 'ask') {
      return `rgba(239, 68, 68, ${alpha})`; // Red for asks
    } else {
      return `rgba(148, 163, 184, ${alpha})`; // Gray for mid
    }
  };

  const handlePriceLevelClick = (priceLevel) => {
    setSelectedPriceLevel(selectedPriceLevel?.price === priceLevel?.price ? null : priceLevel);
  };

  const maxValue = Math.max(...heatmapData?.map(d => viewMode === 'depth' ? d?.liquidity : d?.volume));

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Liquidity Heatmap</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('depth')}
              className={`px-3 py-1 text-xs rounded-md transition-colors duration-200 ${
                viewMode === 'depth' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Depth
            </button>
            <button
              onClick={() => setViewMode('volume')}
              className={`px-3 py-1 text-xs rounded-md transition-colors duration-200 ${
                viewMode === 'volume' ?'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Volume
            </button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-6 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.6)' }}></div>
            <span>Bids</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.6)' }}></div>
            <span>Asks</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="MousePointer" size={12} />
            <span>Click price level for details</span>
          </div>
        </div>
      </div>
      <div className="p-4">
        {/* Heatmap Grid */}
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {heatmapData?.map((level, index) => {
            const value = viewMode === 'depth' ? level?.liquidity : level?.volume;
            const barWidth = (value / maxValue) * 100;
            const isSelected = selectedPriceLevel?.price === level?.price;
            
            return (
              <div
                key={index}
                onClick={() => handlePriceLevelClick(level)}
                className={`
                  relative flex items-center justify-between p-2 rounded cursor-pointer
                  transition-all duration-200 hover:bg-muted/50
                  ${isSelected ? 'ring-2 ring-accent bg-accent/10' : ''}
                `}
                style={{ 
                  backgroundColor: isSelected ? undefined : getIntensityColor(level?.intensity, level?.side)
                }}
              >
                {/* Background bar */}
                <div
                  className="absolute inset-y-0 left-0 rounded transition-all duration-300"
                  style={{
                    width: `${barWidth}%`,
                    backgroundColor: getIntensityColor(Math.min(1, level?.intensity + 0.2), level?.side)
                  }}
                />
                {/* Content */}
                <div className="relative z-10 flex items-center justify-between w-full">
                  <div className="flex items-center gap-3">
                    <span className={`text-sm font-mono font-semibold ${
                      level?.side === 'bid' ? 'text-success' : 
                      level?.side === 'ask' ? 'text-error' : 'text-foreground'
                    }`}>
                      {formatPrice(level?.price)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {level?.orders} orders
                    </span>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-mono text-foreground">
                      {formatQuantity(value)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {viewMode === 'depth' ? 'liquidity' : 'volume'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Price Level Details */}
        {selectedPriceLevel && (
          <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border">
            <h4 className="text-sm font-semibold text-foreground mb-2">
              Price Level Details: {formatPrice(selectedPriceLevel?.price)}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground">Liquidity:</span>
                <div className="font-mono font-semibold text-foreground">
                  {formatQuantity(selectedPriceLevel?.liquidity)}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Volume:</span>
                <div className="font-mono font-semibold text-foreground">
                  {formatQuantity(selectedPriceLevel?.volume)}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Orders:</span>
                <div className="font-mono font-semibold text-foreground">
                  {selectedPriceLevel?.orders}
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Side:</span>
                <div className={`font-semibold capitalize ${
                  selectedPriceLevel?.side === 'bid' ? 'text-success' : 
                  selectedPriceLevel?.side === 'ask' ? 'text-error' : 'text-foreground'
                }`}>
                  {selectedPriceLevel?.side}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiquidityHeatmap;